import React, { useState, useEffect } from "react";
import { Box, useMediaQuery, Breadcrumbs } from "@mui/material";
import {
  Link,
  Outlet,
  useLocation,
  useParams,
  useNavigate,
  Navigate,
} from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import SidebarV2 from "./SidebarV2";
import BackButton from "../components/BackButton";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
// import { useAuth } from "../contexts/AuthContext"; // Removed - auth handled by TokenExpirationGuard
import IdleTimerDialog from "../components/Dialogs/IdleTimerDialog";
import { useBreadCrumbContext } from "../contexts/BreadCrumbContext";
import { useAuth } from "../contexts/AuthContext";
import { findRouteRequirement } from "../utils/routePermissions";
import Forbidden403 from "../pages/Errors/Forbidden403";
import Loader from "../components/Molecules/Loader/Loader";
import { getConfig } from "../config/runtimeConfig";

interface LayoutProps {
  data: {
    name: string;
    occupation: string;
  }; // Update the type of 'data' according to your requirements
}

const ProtectedLayout: React.FC<LayoutProps> = ({ data }) => {
  const { currentBreadcrumbPage } = useBreadCrumbContext();
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // BUG-02: Sidebar should be expanded by default
  const { domainId, id, ruleId }: any = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const [resourceName, setResourceName] = useState("");
  const [resourceId, setResourceId] = useState("");
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setResourceName(searchParams.get("resourceName") || "");
    setResourceId(searchParams.get("resourceId") || "");
  }, [location.search]);

  const breadcrumbData = [
    { path: "/", text: "Home" },
    { path: "/resource/:id", text: "Resources" },
    { path: "/resource/add", text: "Add Resource" },
    { path: "/resource/view/:id", text: "View Resource" },
    { path: "/resource/researchQuery", text: "Research Query" },
    { path: "/resource/edit/:id", text: "Edit Resource" },
    { path: "/resource/validate-resource/:id", text: "Validate Resource" },
    {
      path: "/resource/re-run-validate-resource/:id",
      text: "Re-Run Validate Resource",
    },
    { path: "/resource/auditResource/:id", text: "Resource Audit" },
    // { path: "/resource/validate-result", text: "Validate Result" },
    { path: "/domain", text: "Domains" },
    { path: "/domain/add", text: "Add Domain" },
    { path: "/domain/view/:id", text: "View Domain" },
    { path: "/domain/edit/:id", text: "Edit Domain" },
    { path: "/domain/auditDomain/:id", text: "Domain Audit" },
    { path: "/domain-linkage", text: "Domain Linkages" },
    { path: "/domain-linkage/add", text: "Add Domain Linkage" },
    { path: "/domain-linkage/view", text: "View Domain Linkage" },
    { path: "/domain-linkage/edit/:id", text: "Edit Domain Linkage" },
    {
      path: "/results",
      text: "Run Score History",
      linkTo: "/results/run-score-history",
    },
    {
      path: "/results/run-score-dashboard/:id",
      text: "Run Score Dashboard",
    },
    { path: "/files-list", text: "File List" },
    { path: "/rules-list/:id", text: "Comparison Rules" },
    { path: "/rule-history", text: "Rule History" },
    { path: "/rules/add", text: "Add Rule" },
    { path: "/dashboard", text: "Dashboard" },
    { path: "/dashboard-v2", text: "Dashboard V2" },
    { path: "/research-query", text: "Research Queries" },
    { path: "/research-query/add", text: "Add Research Query" },
    { path: "/research-query/view", text: "View Research Query" },
    { path: "/research-query/edit", text: "Edit Research Query" },
    {
      path: "/research-query/auditGenericResearchQuery/:id",
      text: "Audit Research Query",
    },
    {
      path: "/research-query/research-query-execution/:id",
      text: "Execute Query",
    },
    {
      path: "/research-execution-history/dashboard",
      text: "Research Query Dashboard",
    },
    { path: "/rules/edit/:id", text: "Edit Rule" },
    { path: "/rules/view/:id", text: "View Rule" },
    { path: "/rules/auditRule/:id", text: "Rule Audit" },
    { path: "/rules/researchQuery", text: "Research Query" },
    { path: "/rules-execution-history", text: "Comparison History" },

    { path: "/incident-reporting-rerun", text: "incident-reporting-rerun" },
    {
      path: "/incident-reporting-rerun-report",
      text: "incident-reporting-rerun-report",
    },

    { path: "/rules/rule-execution/:id", text: "Rule Execution" },
    { path: "/rules/re-run-rule-execution/:id", text: "Re-Run Rule Execution" },
    {
      path: "/rules-execution-history/dashboard",
      text: "Comparison Dashboard",
    },
    {
      path: "/rules-execution-history/consolidate-results",
      text: "Comparison Summary",
    },
    { path: "/validation-execution-history", text: "Validation History" },
    {
      path: "/validation-execution-history/validate-result",
      text: "Validate Result",
    },
    {
      path: "/validation-execution-history/validation-execution-results",
      text: "Validation Summary",
    },
    { path: "/resource-columns/:id", text: "Resource Columns" },
    { path: "/resource-columns/add-columns", text: "Add Resource Column" },
    {
      path: "/resource-columns/view/:id",
      text: "View Resource Column",
    },
    {
      path: "/resource-columns/edit/:id",
      text: "Edit Resource Column",
    },
    {
      path: "/resource-columns/auditResourceColumn/:id",
      text: "Resource Column Audit",
    },

    { path: "/linked-services", text: "Linked Services" },
    { path: "/linked-services/add", text: "Add Linked Service" },
    { path: "/linked-services/view/:id", text: "View Linked Service" },
    { path: "/linked-services/edit/:id", text: "Edit Linked Service" },
    {
      path: "/linked-services/auditLinkedServices/:id",
      text: "Linked Service Audit",
    },

    { path: "/run-instance", text: "Execution Groups" },
    { path: "/run-instance/add", text: "Add Execution Group" },
    { path: "/run-instance/view/:id", text: "View Execution Group" },
    { path: "/run-instance/edit/:id", text: "Edit Execution Group" },
    { path: "/execution-sub-group", text: "Execution Sub Group" },
    { path: "/execution-sub-group/add", text: "Add Execution Sub Group" },
    {
      path: "/execution-sub-group/view/:id",
      text: "View Execution Sub Group",
    },
    {
      path: "/execution-sub-group/edit/:id",
      text: "Edit Execution Sub Group",
    },
    {
      path: "/comparison-research-queries",
      text: "Comparison Research Queries",
    },
    {
      path: "/comparison-research-queries/view/:id",
      text: "View Comparison Research Queries",
    },
    {
      // DQ-8540 (re-open) Fix #3: breadcrumb entry for the edit route was
      // missing, causing the breadcrumb to show nothing when editing a
      // comparison research query.
      path: "/comparison-research-queries/edit/:id",
      text: "Edit Comparison Research Queries",
    },
    {
      path: "/comparison-research-queries/manage",
      text: "Manage Comparison Research Queries",
    },
    {
      path: "/comparison-research-queries/auditComparison/:id",
      text: "Audit Comparison Research Queries",
    },
    { path: "/connection-keys", text: "Connection Keys" },
    { path: "/connection-keys/add", text: "Add Connection Key" },
    { path: "/connection-keys/view/:id", text: "View Connection Key" },
    { path: "/connection-keys/edit/:id", text: "Edit Connection Key" },
    { path: "/system", text: "System" },
    { path: "/system/add", text: "Add System" },
    { path: "/system/view/:id", text: "View System" },
    { path: "/system/edit/:id", text: "Edit System" },
    {
      path: "/connection-keys/auditConnectionKey/:id",
      text: "Connection Key Audit",
    },
    { path: "/file-processing-attributes", text: "File Processing Attributes" },
    {
      path: "/file-processing-attributes/add",
      text: "Add File Processing Attributes",
    },
    {
      path: "/file-processing-attributes/view/:id",
      text: "View File Processing Attributes",
    },
    {
      path: "/file-processing-attributes/edit/:id",
      text: "Edit File Processing Attributes",
    },
    {
      path: "/domain/import-entity",
      text: "Import Json",
    },
    {
      path: "/resource/import-entity",
      text: "Import Json",
    },
    {
      path: "/resource-column/import-entity",
      text: "Import Json",
    },
    {
      path: "/rules/import-entity",
      text: "Import Json",
    },
    {
      path: "/research-query/import-entity",
      text: "Import Json",
    },
    {
      path: "/blob-list",
      text: "Blob File List",
    },
    {
      path: "/sftp-list",
      text: "SFTP File List",
    },
    {
      path: "/admin",
      text: "Administrative links",
    },
    {
      path: "/issue-list",
      text: "All Issues List",
    },
    {
      path: "/research-execution-history",
      text: "Research Query History",
    },
    {
      path: "/research-execution-history/re-run-research-query-execution/:id",
      text: "Re-Run Research Query Execution",
    },
    // Role Management breadcrumbs
    { path: "/roles", text: "Role Management" },
    { path: "/roles/add", text: "Add Role" },
    { path: "/roles/view/:id", text: "View Role" },
    { path: "/roles/edit/:id", text: "Edit Role" },
    // Group Management breadcrumbs
    { path: "/groups", text: "Group Management" },
    { path: "/groups/add", text: "Add Group" },
    { path: "/groups/view/:id", text: "View Group" },
    { path: "/groups/edit/:id", text: "Edit Group" },
    { path: "/groups/:id/assign-users", text: "Assign Users" },

    // User Management breadcrumbs
    { path: "/users", text: "User Management" },
    { path: "/users/add", text: "Add User" },
    { path: "/users/view/:id", text: "View User" },
    { path: "/users/edit/:id", text: "Edit User" },
    // Tag Management breadcrumbs
    { path: "/tags", text: "Tag Management" },
    { path: "/tags/add", text: "Add Tag" },
    { path: "/tags/view/:id", text: "View Tag" },
    { path: "/tags/edit/:id", text: "Edit Tag" },

    { path: "/connection-runtime-config", text: "Connection Runtime Config" },
    {
      path: "/connection-runtime-config/add",
      text: "Add Connection Runtime Config",
    },
    {
      path: "/connection-runtime-config/view/:id",
      text: "View Connection Runtime Config",
    },
    {
      path: "/connection-runtime-config/edit/:id",
      text: "Edit Connection Runtime Config",
    },

    // Profile breadcrumb
    { path: "/profile", text: "Profile" },
    { path: "/profile/change-password", text: "Change Password" },

    { path: "/classification", text: "Classification" },
    { path: "/classification/add", text: "Add Classification" },
    { path: "/classification/view/:id", text: "View Classification" },
    { path: "/classification/edit/:id", text: "Edit Classification" },

    { path: "/reason", text: "Reason" },
    { path: "/reason/add", text: "Add Reason" },
    { path: "/reason/view/:id", text: "View Reason" },
    { path: "/reason/edit/:id", text: "Edit Reason" },

    // Scoring breadcrumbs.
    // List-level entries use `linkTo` to redirect into the consolidated
    // `/scoring-config` tabbed view (the standalone list routes are gone).
    // Existing query string (e.g. status_filter=Active) is preserved at
    // render time; see Link rendering below.
    { path: "/scoring-config", text: "Scoring" },
    {
      path: "/scoring/validation",
      text: "Validation Scoring",
      linkTo: "/scoring-config?tab=0&runSub=0",
    },
    { path: "/scoring/validation/add", text: "Add Validation Scoring" },
    { path: "/scoring/validation/edit/:id", text: "Edit Validation Scoring" },
    { path: "/scoring/validation/view/:id", text: "View Validation Scoring" },
    { path: "/scoring/validation/audit/:id", text: "Validation Scoring Audit" },
    {
      path: "/scoring/comparison",
      text: "Comparison Scoring",
      linkTo: "/scoring-config?tab=0&runSub=1",
    },
    { path: "/scoring/comparison/add", text: "Add Comparison Scoring" },
    { path: "/scoring/comparison/edit/:id", text: "Edit Comparison Scoring" },
    { path: "/scoring/comparison/view/:id", text: "View Comparison Scoring" },
    { path: "/scoring/comparison/audit/:id", text: "Comparison Scoring Audit" },
    // Validation Run Scoring breadcrumbs
    {
      path: "/scoring/validation-run",
      text: "Validation Run Scoring",
      linkTo: "/scoring-config?tab=1&aggSub=0",
    },
    { path: "/scoring/validation-run/add", text: "Add Validation Run Scoring" },
    {
      path: "/scoring/validation-run/edit/:id",
      text: "Edit Validation Run Scoring",
    },
    {
      path: "/scoring/validation-run/view/:id",
      text: "View Validation Run Scoring",
    },
    {
      path: "/scoring/validation-run/audit/:runId/:resourceId",
      text: "Validation Run Scoring Audit",
    },
    // Comparison Run Scoring breadcrumbs
    {
      path: "/scoring/comparison-run",
      text: "Comparison Run Scoring",
      linkTo: "/scoring-config?tab=1&aggSub=1",
    },
    {
      path: "/scoring/comparison-run/add",
      text: "Add Comparison Run Scoring",
    },
    {
      path: "/scoring/comparison-run/edit/:id",
      text: "Edit Comparison Run Scoring",
    },
    {
      path: "/scoring/comparison-run/view/:id",
      text: "View Comparison Run Scoring",
    },
    // Generic RQ Scoring breadcrumbs
    {
      path: "/scoring/generic-rq",
      text: "Generic RQ Scoring",
      linkTo: "/scoring-config?tab=0&runSub=2",
    },
    { path: "/scoring/generic-rq/add", text: "Add Generic RQ Scoring" },
    { path: "/scoring/generic-rq/edit/:id", text: "Edit Generic RQ Scoring" },
    { path: "/scoring/generic-rq/view/:id", text: "View Generic RQ Scoring" },
    {
      path: "/scoring/generic-rq/audit/:id",
      text: "Generic RQ Scoring Audit",
    },
    // Research Query Run Scoring breadcrumbs
    {
      path: "/scoring/research-query-run",
      text: "Research Query Run Scoring",
      linkTo: "/scoring-config?tab=1&aggSub=2",
    },
    {
      path: "/scoring/research-query-run/add",
      text: "Add Research Query Run Scoring",
    },
    {
      path: "/scoring/research-query-run/edit/:id",
      text: "Edit Research Query Run Scoring",
    },
    {
      path: "/scoring/research-query-run/view/:id",
      text: "View Research Query Run Scoring",
    },
    // Run Scoring Overview breadcrumb
    {
      path: "/scoring/run-overview/:runInstanceId",
      text: "Run Scoring Overview",
    },
    // Pipeline Weight Config breadcrumbs
    {
      path: "/scoring/pipeline-weights",
      text: "Pipeline Weight Config",
      linkTo: "/scoring-config?tab=1&aggSub=3",
    },
    {
      path: "/scoring/pipeline-weights/add",
      text: "Add Pipeline Weight Config",
    },
    {
      path: "/scoring/pipeline-weights/edit/:id",
      text: "Edit Pipeline Weight Config",
    },
    {
      path: "/scoring/pipeline-weights/view/:id",
      text: "View Pipeline Weight Config",
    },
    // Scoring Defaults breadcrumb (system-wide default scoring parameters,
    // managed via env vars on the backend; tab state is preserved via the
    // page's own ?tab=&runSub=&aggSub= query params).
    { path: "/scoring-defaults", text: "Scoring Defaults" },
  ];

  const getCurrentBreadcrumbs = (location: any) => {
    const pathSegments = location.pathname
      .split("/")
      .filter((path: any) => path !== "" && path !== "all");

    // Special handling for paths with ID and additional segments (like /groups/:id/assign-users)
    const originalPath = location.pathname;

    // Check if this is a special pattern like /groups/:id/assign-users
    const specialPatterns = [
      {
        pattern: /^\/groups\/\d+\/assign-users$/,
        template: "/groups/:id/assign-users",
      },
    ];

    for (const { pattern, template } of specialPatterns) {
      if (pattern.test(originalPath)) {
        const breadcrumb = breadcrumbData.find(
          (item) => item.path === template,
        );
        if (breadcrumb) {
          return [
            breadcrumbData.find((item) => item.path === "/groups"),
            breadcrumb,
          ].filter(Boolean);
        }
      }
    }

    // For system routes, filter out the ID parameter (last segment if it's after view/edit)
    const pathnames = pathSegments.filter((path: any, index: any) => {
      // Filter out numeric IDs
      if (isNaN(path)) {
        // For system routes, also filter out the ID parameter after view/edit
        if (
          index > 0 &&
          (pathSegments[index - 1] === "view" ||
            pathSegments[index - 1] === "edit")
        ) {
          const parentPath = pathSegments.slice(0, index - 1).join("/");
          if (parentPath === "system") {
            return false; // Filter out the system ID parameter
          }
        }
        return true;
      }
      return false; // Filter out numeric paths
    });

    // Extract query parameters
    const queryParams = new URLSearchParams(location.search);

    return pathnames.map((path: any, index: any) => {
      const url = `/${pathnames.slice(0, index + 1).join("/")}`;
      // First, try to find an exact match
      let breadcrumb: any = breadcrumbData.find((item) => {
        const basePath = item.path.split("/:")[0];
        return basePath === url;
      });

      // If no exact match, find substring matches but exclude paths with additional segments
      if (!breadcrumb) {
        breadcrumb = breadcrumbData.find((item) => {
          const basePath = item.path.split("/:")[0];
          // Match if basePath contains url but doesn't have additional path segments after url
          // This allows /rules-list to match /rules but prevents /research-execution-history/dashboard from matching /research-execution-history
          return basePath.includes(url) && !basePath.startsWith(url + "/");
        });
      }

      // Replace placeholder in path with actual values
      if (domainId) {
        breadcrumb = {
          ...breadcrumb,
          path: breadcrumb?.path?.replace(":id", domainId),
        };
      } else if (ruleId) {
        breadcrumb = {
          ...breadcrumb,
          path: breadcrumb?.path?.replace(":id", id),
        };
      } else {
        breadcrumb = {
          ...breadcrumb,
          path: breadcrumb?.path?.replace("/:id", "/all"),
        };
      }

      // Replace placeholder in path with actual values
      if (id) {
        breadcrumb = {
          ...breadcrumb,
          path: breadcrumb?.path?.replace(":id", id),
        };
      }

      return breadcrumb;
    });
  };

  const { state } = useLocation();
  const breadcrumbs = getCurrentBreadcrumbs(location);
  const drawerWidth = 280; // BUG-03: Sidebar width should be 280px to match Reference
  const closedDrawerWidth = 81;
  const MemoNavbar = React.memo(Navbar);
  // DQ-8763: the AutoXLR8 Theme-2 navy sidebar is the default shell. The legacy
  // sidebar stays reachable by setting REACT_APP_SIDEBAR_V2=false.
  const useSidebarV2 = getConfig("REACT_APP_SIDEBAR_V2") !== "false";

  // Token checking is now handled by TokenExpirationGuard wrapper
  // Save current path for potential redirect after login (per-tab)
  try {
    sessionStorage.setItem("lastVisitedPath", window.location.pathname);
  } catch {}

  const handleClick = (name: any, id: any) => {
    if (state?.details?.url) {
      navigate(state.details.url, {
        state: {
          details: {
            name,
            id,
          },
        },
      });
    }
  };

  const { hasPermission, isAdmin, isPermissionsLoading, allowedPolicies } =
    useAuth();
  const req = findRouteRequirement(location.pathname);

  // Handle authorization based on route requirements
  let isAuthorized = true;
  if (req) {
    if (req.adminOnly) {
      // Admin-only routes require isAdmin = true
      isAuthorized = isAdmin;
    } else {
      // Regular routes use permission-based authorization
      isAuthorized = hasPermission(req.entity, req.action);
    }
  }

  // Check if user has no policies and is trying to access a protected route
  const hasNoPolicies = !isPermissionsLoading && allowedPolicies.length === 0;
  const isProfilePage = location.pathname === "/profile";
  const isDashboardPage = location.pathname === "/dashboard";

  // Redirect to profile if user has no policies and is not already on profile page or dashboard page
  // Dashboard should be accessible to all authenticated users regardless of permissions
  if (hasNoPolicies && !isProfilePage && !isDashboardPage) {
    return <Navigate to="/profile" replace />;
  }

  // Show loader while permissions are being fetched
  const shouldShowLoader = isPermissionsLoading && !!req; // Only show loader if route requires permissions
  const shouldShow403 =
    !isPermissionsLoading && !isAuthorized && !hasNoPolicies; // Only show 403 after loading is complete and user has policies but lacks specific permission

  const pageContent = (
          <Box className="page-view">
            {shouldShowLoader ? (
              <Loader isLoading={shouldShowLoader} />
            ) : shouldShow403 ? (
              <Forbidden403 />
            ) : (
              <>
                {/* BREADCRUMB WITH INLINE BACK BUTTON */}
                <Box sx={{ display: "flex", alignItems: "top", gap: 1, mb: 0 }}>
                  {/* BACK BUTTON - Positioned first, inline with breadcrumb */}
                  <BackButton
                    size="medium"
                    className="breadcrumb-back-button"
                    tooltipText="Go back to previous page"
                  />

                  {/* BREADCRUMB NAVIGATION */}
                  <Breadcrumbs
                    aria-label="breadcrumb"
                    separator={<NavigateNextIcon fontSize="small" />}
                    className="breadcrumb"
                    sx={{ flex: 1 }}
                  >
                    {breadcrumbs.map((breadcrumb: any, index: any) => {
                      // Prefer explicit `linkTo` (used by scoring list-level
                      // entries that redirect into the consolidated
                      // /scoring-config tabbed view). When present, append
                      // the current location.search so filter params like
                      // `status_filter=Active` survive the navigation.
                      let to = breadcrumb.path;
                      if (breadcrumb.linkTo) {
                        to = breadcrumb.linkTo;
                        const search = location.search;
                        if (search) {
                          to += to.includes("?")
                            ? `&${search.slice(1)}`
                            : search;
                        }
                      }
                      return (
                        <Link
                          key={index}
                          color="inherit"
                          to={to}
                          onClick={(event: any) => {
                            if (index === breadcrumbs.length - 1) {
                              event.preventDefault();
                            }
                          }}
                          className={
                            index === breadcrumbs.length - 1 ? "active" : ""
                          }
                        >
                          {breadcrumb.text}
                        </Link>
                      );
                    })}
                    {currentBreadcrumbPage?.id && (
                      <Box className="active">
                        <span className="state-name">
                          {currentBreadcrumbPage.url ? (
                            <Box
                              onClick={() =>
                                navigate(`${currentBreadcrumbPage.url}`)
                              }
                              className="active cursor-pointer"
                            >
                              {currentBreadcrumbPage.name} (
                              {currentBreadcrumbPage.id})
                            </Box>
                          ) : (
                            `${currentBreadcrumbPage.name} (${currentBreadcrumbPage.id})`
                          )}
                        </span>
                      </Box>
                    )}
                    {(resourceName || resourceId) && (
                      <span className="state-name">
                        <span className="icon-sep"></span>
                        {resourceName}&nbsp;({resourceId})
                      </span>
                    )}
                    {state && state.details && (
                      <span className="state-name">
                        <span className="icon-sep"></span>
                        {state?.details?.url ? (
                          <Box
                            onClick={() =>
                              handleClick(
                                state?.details?.name,
                                state?.details?.id,
                              )
                            }
                            className="active cursor-pointer"
                          >
                            {state?.details?.name}&nbsp;({state?.details?.id})
                          </Box>
                        ) : (
                          <>
                            {state?.details?.name}&nbsp;({state?.details?.id})
                          </>
                        )}
                      </span>
                    )}
                  </Breadcrumbs>
                </Box>

                <Outlet />
              </>
            )}
          </Box>
  );

  // DQ-8763: SidebarV2 lays the shell out with CSS flex (.app-shell /
  // .left-panel / .right-panel) instead of the legacy width-calculated boxes,
  // so the top bar moves inside the right panel and the sidebar owns the logo.
  if (useSidebarV2) {
    return (
      <Box className="app-shell">
        {/* <IdleTimerDialog /> */}
        <SidebarV2
          user={data || {}}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />

        <Box className="right-panel">
          <MemoNavbar
            user={data || {}}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            showLogo={false}
          />
          <Box className="main-container">{pageContent}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <MemoNavbar
        user={data || {}}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <Box display={isNonMobile ? "flex" : "flex"} width="100%" height="100%">
        {/* <IdleTimerDialog /> */}
        <Sidebar
          user={data || {}}
          isNonMobile={isNonMobile}
          drawerWidth={drawerWidth}
          closedDrawerWidth={closedDrawerWidth}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />

        <Box
          flexGrow={1}
          style={
            isSidebarOpen && isNonMobile
              ? { maxWidth: `calc(100% - ${drawerWidth}px)` }
              : !isSidebarOpen && !isNonMobile
                ? { maxWidth: "calc(100% - 13px)" }
                : isSidebarOpen && !isNonMobile
                  ? { maxWidth: `calc(100% - 0px)` }
                  : { maxWidth: "calc(100% - 81px)" }
          }
        >
          {pageContent}
        </Box>
      </Box>
    </>
  );
};

export default ProtectedLayout;
